import React, { useState, useCallback, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { 
  $getSelection, 
  $isRangeSelection, 
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  $createParagraphNode,
  $isElementNode
} from 'lexical';
import { 
  INSERT_UNORDERED_LIST_COMMAND, 
  INSERT_ORDERED_LIST_COMMAND,
  $isListNode
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $findMatchingParent } from '@lexical/utils';
import { $setBlocksType } from '@lexical/selection';

function LexicalToolbar() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [currentBlockType, setCurrentBlockType] = useState('paragraph');
  const [isBulletList, setIsBulletList] = useState(false);
  const [isNumberedList, setIsNumberedList] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      
      // Check if selection is in a link
      const nodes = selection.getNodes();
      let foundLink = false;
      for (const node of nodes) {
        const linkNode = $findMatchingParent(node, $isLinkNode);
        if (linkNode !== null) {
          foundLink = true;
          break;
        }
      }
      setIsLink(foundLink);
      
      // Check block type and list state
      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      
      // Check if we're in a list by finding the parent list node
      const parentList = $findMatchingParent(anchorNode, $isListNode);
      if (parentList !== null) {
        const listType = parentList.getListType();
        setIsBulletList(listType === 'bullet');
        setIsNumberedList(listType === 'number');
        setCurrentBlockType(listType);
      } else if ($isElementNode(element) && $isListNode(element)) {
        const listType = element.getListType();
        setIsBulletList(listType === 'bullet');
        setIsNumberedList(listType === 'number');
        setCurrentBlockType(listType);
      } else {
        setIsBulletList(false);
        setIsNumberedList(false);
        if ($isElementNode(element)) {
          const type = element.getType();
          if (['h1', 'h2', 'h3'].includes(type)) {
            setCurrentBlockType(type);
          } else if (type === 'quote') {
            setCurrentBlockType('quote');
          } else {
            setCurrentBlockType('paragraph');
          }
        }
      }
    }
  }, []); // editor dependency removed - updateToolbar reads editor state directly

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);


  const formatText = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatList = (listType) => {
    if (listType === 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else if (listType === 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const formatHeading = (headingSize) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (headingSize === 'paragraph') {
          $setBlocksType(selection, () => $createParagraphNode());
        } else if (headingSize === 'quote') {
          $setBlocksType(selection, () => $createQuoteNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      }
    });
  };

  const handleLink = () => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      setShowLinkDialog(true);
    }
  };

  const insertLink = () => {
    if (linkUrl.trim()) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
      setShowLinkDialog(false);
      setLinkUrl('');
    }
  };

  const undo = () => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  };

  const redo = () => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  };

  return (
    <>
      <div className="lexical-toolbar">
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-button"
            onClick={undo}
            title="Undo (Ctrl+Z)"
          >
            <i className="fas fa-undo"></i>
          </button>
          <button
            type="button"
            className="toolbar-button"
            onClick={redo}
            title="Redo (Ctrl+Y)"
          >
            <i className="fas fa-redo"></i>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            className={`toolbar-button ${isBold ? 'active' : ''}`}
            onClick={() => formatText('bold')}
            title="Bold"
          >
            <i className="fas fa-bold"></i>
          </button>
          <button
            type="button"
            className={`toolbar-button ${isItalic ? 'active' : ''}`}
            onClick={() => formatText('italic')}
            title="Italic"
          >
            <i className="fas fa-italic"></i>
          </button>
          <button
            type="button"
            className={`toolbar-button ${isUnderline ? 'active' : ''}`}
            onClick={() => formatText('underline')}
            title="Underline"
          >
            <i className="fas fa-underline"></i>
          </button>
        </div>
        
        <div className="toolbar-group">
          <div className="toolbar-dropdown">
            <select
              value={currentBlockType}
              onChange={(e) => formatHeading(e.target.value)}
              className="toolbar-select"
              title="Text Style"
            >
              <option value="paragraph">Paragraph</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="quote">Quote</option>
            </select>
          </div>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            className={`toolbar-button ${isLink ? 'active' : ''}`}
            onClick={handleLink}
            title="Insert Link"
          >
            <i className="fas fa-link"></i>
          </button>
        </div>
        
        <div className="toolbar-group">
          <button
            type="button"
            className={`toolbar-button ${isBulletList ? 'active' : ''}`}
            onClick={() => formatList('bullet')}
            title="Bullet List"
          >
            <i className="fas fa-list-ul"></i>
          </button>
          <button
            type="button"
            className={`toolbar-button ${isNumberedList ? 'active' : ''}`}
            onClick={() => formatList('number')}
            title="Numbered List"
          >
            <i className="fas fa-list-ol"></i>
          </button>
        </div>
      </div>

      {showLinkDialog && (
        <div className="link-dialog-overlay" onClick={() => setShowLinkDialog(false)}>
          <div className="link-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Link</h3>
            <input
              type="text"
              placeholder="Enter URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  insertLink();
                } else if (e.key === 'Escape') {
                  setShowLinkDialog(false);
                  setLinkUrl('');
                }
              }}
              autoFocus
            />
            <div className="link-dialog-buttons">
              <button onClick={insertLink}>Insert</button>
              <button onClick={() => {
                setShowLinkDialog(false);
                setLinkUrl('');
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LexicalToolbar;
